import { Sparkles } from 'lucide-react';
import type { StudyPageTab, StudyTabContext } from '../study/StudyPage';
import { AiPanel } from './AiPanel';
import { type ActionsBase, shareBase, useImageActions } from './api';

/**
 * Whether the tab is shown at all: only when the image's workspace has turned
 * an action on (decided 2026-10-05: nobody sees an AI tab with nothing in it).
 * The same list the panel reads, so no second request. While the list has
 * not answered (loading, or an image the page does not know) there is no
 * answer, and the page keeps the one it had: the tab is out until the first
 * yes, and does not blink out on every image change.
 */
export function useAiTabVisible(ctx: Pick<StudyTabContext, 'imageId'>, base: ActionsBase = ''): boolean | undefined {
  const actions = useImageActions(ctx.imageId, base);
  if (actions.data === undefined) return undefined;
  return actions.data.some((a) => a.enabled === true);
}

/**
 * The AI tab of the study page's right panel, beside Annotations and
 * Information (decision 14), with the sparkle the AI badge wears. It appears
 * only where an action is turned on (useAiTabVisible).
 *
 * With `shareId`, the tab as a share link shows it (user, 2026-10-12): the
 * results, read through the link's namespace, and nothing that runs or
 * cancels anything, whoever holds the link. A shell never gives the tab to
 * somebody who reads the study only because it is Public: a public image
 * shows no AI (the server refuses those reads too, actions.ts).
 */
export function aiTab({ shareId }: { shareId?: string } = {}): StudyPageTab {
  const base = shareId ? shareBase(shareId) : '';
  return {
    id: 'ai',
    title: 'AI',
    useVisible: (ctx) => useAiTabVisible(ctx, base),
    icon: <Sparkles className="h-4 w-4" aria-hidden />,
    label: (
      <span className="inline-flex items-center gap-1">
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
        AI
      </span>
    ),
    render: (ctx) => (
      <AiPanel
        // One panel per image: a run's dialog or a failed cancel belongs to the image it was on.
        key={ctx.imageId ?? ctx.seriesUID ?? ''}
        engine={ctx.engine}
        noEngineReason={ctx.noEngineReason}
        imageId={ctx.imageId}
        imageKind={ctx.imageKind}
        studyUID={ctx.studyUID}
        seriesUID={ctx.seriesUID}
        base={base}
        // Starting and cancelling need a write role in the image's workspace;
        // until the page knows, nobody is offered it (the server decides
        // anyway). A link grants it to nobody.
        canRun={!shareId && ctx.readOnly === false}
      />
    ),
  };
}

/** The AI tab as both shells show it for the study's own workspace. */
export const aiStudyTab: StudyPageTab = aiTab();

/**
 * The AI tab a community study page gets (user, 2026-10-12): through a share
 * link, its results read only; for a member of the study's workspace, the
 * workspace's own; and none for somebody who reads the study only because it
 * is Public.
 */
export function aiTabsFor({ shareId, member }: { shareId?: string; member: boolean }): StudyPageTab[] {
  if (shareId) return [aiTab({ shareId })];
  return member ? [aiStudyTab] : [];
}
